import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import MarkdownEditor from '../components/MarkdownEditor';
import MarkdownRenderer from '../components/MarkdownRenderer';

export type TaskStatus = 'todo' | 'in_progress' | 'done';

interface Company {
  id: number;
  company_name: string | null;
  name: string;
}

interface Project {
  id: number;
  company_id: number | null;
  company_name?: string | null;
  company_contact_name?: string | null;
  shepherd_id: number | null;
  shepherd_name?: string | null;
  shepherd_email?: string | null;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  total_tasks?: number;
  tasks_todo?: number;
  tasks_in_progress?: number;
  tasks_done?: number;
}

interface Agent {
  id: number;
  name: string | null;
  email: string;
}

interface Task {
  id: number;
  project_id: number;
  assigned_to: number | null;
  assignee_name?: string | null;
  assignee_email?: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  sort_order: number;
  due_date: string | null;
  created_at: string;
  updated_at: string;
}

interface TaskWithProject extends Task {
  project_name: string;
}

interface TaskTimeEntry {
  id: number;
  task_id: number;
  user_id: number;
  start_time: string | null;
  stop_time: string | null;
  duration_minutes: number;
  notes: string | null;
  created_at: string;
  user_name?: string | null;
  user_email?: string | null;
}

interface TaskComment {
  id: number;
  task_id: number;
  user_id: number;
  message: string;
  created_at: string;
  user_name?: string | null;
  user_email?: string | null;
}

const COLUMNS: { id: TaskStatus; label: string; color: string }[] = [
  { id: 'todo', label: 'To Do', color: 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' },
  { id: 'done', label: 'Done', color: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' },
];

type ViewMode = 'table' | 'kanban';
type ProjectsListViewMode = 'cards' | 'table';

const PROJECTS_VIEW_STORAGE_KEY = 'nxtportal.projectsListView';

function getStoredProjectsListView(): ProjectsListViewMode {
  try {
    const saved = localStorage.getItem(PROJECTS_VIEW_STORAGE_KEY);
    if (saved === 'cards' || saved === 'table') return saved;
  } catch {
    // ignore
  }
  return 'cards';
}

const PROJECT_TASKS_VIEW_STORAGE_KEY = 'nxtportal.projectTasksView';

function getStoredProjectTasksView(): ViewMode {
  try {
    const saved = localStorage.getItem(PROJECT_TASKS_VIEW_STORAGE_KEY);
    if (saved === 'table' || saved === 'kanban') return saved;
  } catch {
    // ignore
  }
  return 'kanban';
}

const InternalProjects: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isProjectView = Boolean(projectId);

  // Projects list state
  const [projects, setProjects] = useState<Project[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [showAddProject, setShowAddProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectCompanyId, setNewProjectCompanyId] = useState<string>('');
  const [newProjectShepherdId, setNewProjectShepherdId] = useState<string>('');
  const [projectSubmitting, setProjectSubmitting] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectDescription, setEditProjectDescription] = useState('');
  const [editProjectCompanyId, setEditProjectCompanyId] = useState<string>('');
  const [editProjectShepherdId, setEditProjectShepherdId] = useState<string>('');
  const [editProjectSubmitting, setEditProjectSubmitting] = useState(false);
  const [showEditProjectForm, setShowEditProjectForm] = useState(false);
  const [projectsListViewMode, setProjectsListViewMode] = useState<ProjectsListViewMode>(getStoredProjectsListView);
  const [companyFilter, setCompanyFilter] = useState<string>('');

  // My assigned tasks (list view)
  const [myTasks, setMyTasks] = useState<TaskWithProject[]>([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);

  // Project detail state
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [moveTargetId, setMoveTargetId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredProjectTasksView);
  const [assignedToFilter, setAssignedToFilter] = useState<string>('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [newAssignedTo, setNewAssignedTo] = useState<string>('');
  const [newDueDate, setNewDueDate] = useState<string>('');
  const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingTask, setEditingTask] = useState<Partial<Task> | null>(null);
  const [taskTimeEntries, setTaskTimeEntries] = useState<TaskTimeEntry[]>([]);
  const [showTaskTimeEntryForm, setShowTaskTimeEntryForm] = useState(false);
  const [taskTimeEntryMode, setTaskTimeEntryMode] = useState<'start_stop' | 'direct'>('start_stop');
  const [taskTimeEntryStart, setTaskTimeEntryStart] = useState('');
  const [taskTimeEntryStop, setTaskTimeEntryStop] = useState('');
  const [taskTimeEntryDuration, setTaskTimeEntryDuration] = useState('');
  const [taskTimeEntryNotes, setTaskTimeEntryNotes] = useState('');
  const [taskTimeEntryUserId, setTaskTimeEntryUserId] = useState<string>('');
  const [editingTimeEntryId, setEditingTimeEntryId] = useState<number | null>(null);
  const [editingTimeEntry, setEditingTimeEntry] = useState<Partial<TaskTimeEntry> | null>(null);
  const [editTaskSubmitting, setEditTaskSubmitting] = useState(false);
  const [taskComments, setTaskComments] = useState<TaskComment[]>([]);
  const [newCommentMessage, setNewCommentMessage] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const res = await api.get('/internal/projects/projects');
      setProjects(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data || []);
    } catch {
      setCompanies([]);
    }
  };

  const fetchProject = async (id: number) => {
    try {
      const res = await api.get(`/internal/projects/projects/${id}`);
      setProject(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load project');
      setProject(null);
    }
  };

  const fetchTasks = async (id: number) => {
    try {
      setError(null);
      const res = await api.get(`/internal/projects/projects/${id}/tasks`);
      setTasks(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchCompanies();
    fetchAgents();
  }, []);

  useEffect(() => {
    if (!isProjectView) fetchMyTasks();
  }, [isProjectView]);

  // Pre-fill company and show new project form when navigating from company page (e.g. "New project" from company Projects tab)
  useEffect(() => {
    if (!isProjectView && location.state && typeof (location.state as { newProjectCompanyId?: string }).newProjectCompanyId === 'string') {
      const companyId = (location.state as { newProjectCompanyId: string }).newProjectCompanyId;
      setNewProjectCompanyId(companyId);
      setShowAddProject(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [isProjectView, location.state, location.pathname, navigate]);

  const fetchAgents = async () => {
    try {
      const res = await api.get('/internal/projects/agents');
      setAgents(res.data || []);
    } catch {
      setAgents([]);
    }
  };

  const fetchMyTasks = async () => {
    try {
      setMyTasksLoading(true);
      const res = await api.get('/internal/projects/my-tasks');
      setMyTasks(res.data || []);
    } catch {
      setMyTasks([]);
    } finally {
      setMyTasksLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    const id = parseInt(projectId, 10);
    if (Number.isNaN(id)) {
      setLoading(false);
      setError('Invalid project');
      return;
    }
    setLoading(true);
    setError(null);
    fetchProject(id);
    fetchTasks(id);
    fetchAgents();
  }, [projectId]);

  // Open a specific task when navigating from My Assigned Tasks (e.g. state: { openTaskId })
  useEffect(() => {
    const openTaskId = (location.state as { openTaskId?: number })?.openTaskId;
    if (!isProjectView || loading || !openTaskId || tasks.length === 0) return;
    const task = tasks.find((t) => t.id === openTaskId);
    if (task) {
      openEditTask(task);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [isProjectView, loading, tasks, location.state, location.pathname, navigate]);

  const handleAddProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;
    setProjectSubmitting(true);
    try {
      const res = await api.post('/internal/projects/projects', {
        name: newProjectName.trim(),
        description: newProjectDescription.trim() || null,
        company_id: newProjectCompanyId ? parseInt(newProjectCompanyId, 10) : null,
        shepherd_id: newProjectShepherdId ? parseInt(newProjectShepherdId, 10) : null,
      });
      setNewProjectName('');
      setNewProjectDescription('');
      setNewProjectCompanyId('');
      setNewProjectShepherdId('');
      setShowAddProject(false);
      await fetchProjects();
      navigate(`/internal/projects/${res.data.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create project');
    } finally {
      setProjectSubmitting(false);
    }
  };

  const handleDeleteProject = async (id: number) => {
    if (!window.confirm('Delete this project and all its tasks?')) return;
    try {
      await api.delete(`/internal/projects/projects/${id}`);
      await fetchProjects();
      if (projectId && parseInt(projectId, 10) === id) {
        navigate('/internal/projects');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete project');
    }
  };

  const startEditProject = (proj: Project) => {
    setEditingProjectId(proj.id);
    setEditProjectName(proj.name);
    setEditProjectDescription(proj.description ?? '');
    setEditProjectCompanyId(proj.company_id != null ? String(proj.company_id) : '');
    setEditProjectShepherdId(proj.shepherd_id != null ? String(proj.shepherd_id) : '');
    setShowEditProjectForm(true);
  };

  const cancelEditProject = () => {
    setEditingProjectId(null);
    setShowEditProjectForm(false);
  };

  const handleEditProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProjectId == null || !editProjectName.trim()) return;
    setEditProjectSubmitting(true);
    try {
      const res = await api.patch(`/internal/projects/projects/${editingProjectId}`, {
        name: editProjectName.trim(),
        description: editProjectDescription.trim() || null,
        company_id: editProjectCompanyId ? parseInt(editProjectCompanyId, 10) : null,
        shepherd_id: editProjectShepherdId ? parseInt(editProjectShepherdId, 10) : null,
      });
      if (projectId && parseInt(projectId, 10) === editingProjectId) {
        setProject(res.data);
      }
      cancelEditProject();
      await fetchProjects();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update project');
    } finally {
      setEditProjectSubmitting(false);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !projectId) return;
    setSubmitting(true);
    try {
      await api.post(`/internal/projects/projects/${projectId}/tasks`, {
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        status: 'todo',
        assigned_to: newAssignedTo ? parseInt(newAssignedTo, 10) : null,
        due_date: newDueDate ? new Date(newDueDate).toISOString() : null,
      });
      setNewTitle('');
      setNewDescription('');
      setNewAssignedTo('');
      setNewDueDate('');
      setShowAddTask(false);
      await fetchTasks(parseInt(projectId, 10));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignTask = async (taskId: number, assignedTo: number | null) => {
    if (!projectId) return;
    try {
      await api.patch(`/internal/projects/tasks/${taskId}`, { assigned_to: assignedTo });
      await fetchTasks(parseInt(projectId, 10));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update assignee');
    }
  };

  const handleMoveTask = async (taskId: number, newStatus: TaskStatus) => {
    if (!projectId) return;
    setMoveTargetId(taskId);
    try {
      await api.patch(`/internal/projects/tasks/${taskId}`, { status: newStatus });
      await fetchTasks(parseInt(projectId, 10));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to move task');
    } finally {
      setMoveTargetId(null);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Delete this task?')) return;
    if (!projectId) return;
    try {
      await api.delete(`/internal/projects/tasks/${taskId}`);
      await fetchTasks(parseInt(projectId, 10));
      if (editingTaskId === taskId) {
        setEditingTaskId(null);
        setEditingTask(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete task');
    }
  };

  const fetchTaskTimeEntries = async (taskId: number) => {
    try {
      const res = await api.get(`/internal/projects/tasks/${taskId}/time-entries`);
      setTaskTimeEntries(res.data || []);
    } catch {
      setTaskTimeEntries([]);
    }
  };

  const fetchTaskComments = async (taskId: number) => {
    try {
      const res = await api.get(`/internal/projects/tasks/${taskId}/comments`);
      setTaskComments(res.data || []);
    } catch {
      setTaskComments([]);
    }
  };

  const openEditTask = (task: Task) => {
    setEditingTaskId(task.id);
    setEditingTask({
      title: task.title,
      description: task.description ?? '',
      status: task.status,
      assigned_to: task.assigned_to,
      due_date: task.due_date ? task.due_date.slice(0, 16) : '',
    });
    setTaskTimeEntries([]);
    setTaskComments([]);
    setNewCommentMessage('');
    setShowTaskTimeEntryForm(false);
    setEditingTimeEntryId(null);
    setEditingTimeEntry(null);
    fetchTaskTimeEntries(task.id);
    fetchTaskComments(task.id);
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
    setEditingTask(null);
    setShowTaskTimeEntryForm(false);
    setEditingTimeEntryId(null);
    setEditingTimeEntry(null);
    setNewCommentMessage('');
  };

  const handleAddTaskComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTaskId || !newCommentMessage.trim()) return;
    setCommentSubmitting(true);
    try {
      await api.post(`/internal/projects/tasks/${editingTaskId}/comments`, { message: newCommentMessage.trim() });
      setNewCommentMessage('');
      fetchTaskComments(editingTaskId);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleSaveEditTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTaskId == null || !editingTask?.title?.trim()) return;
    setEditTaskSubmitting(true);
    try {
      await api.patch(`/internal/projects/tasks/${editingTaskId}`, {
        title: editingTask.title.trim(),
        description: editingTask.description?.trim() || null,
        status: editingTask.status,
        assigned_to: editingTask.assigned_to ?? null,
        due_date: editingTask.due_date ? new Date(editingTask.due_date).toISOString() : null,
      });
      await fetchTasks(parseInt(projectId!, 10));
      cancelEditTask();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update task');
    } finally {
      setEditTaskSubmitting(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const handleAddTaskTimeEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTaskId) return;
    try {
      const payload: { notes?: string | null; start_time?: string; stop_time?: string; duration_minutes?: number; user_id?: number } = { notes: taskTimeEntryNotes || null };
      if (taskTimeEntryMode === 'start_stop') {
        if (!taskTimeEntryStart || !taskTimeEntryStop) return;
        payload.start_time = new Date(taskTimeEntryStart).toISOString();
        payload.stop_time = new Date(taskTimeEntryStop).toISOString();
      } else {
        if (!taskTimeEntryDuration || parseFloat(taskTimeEntryDuration) <= 0) return;
        payload.duration_minutes = Math.round(parseFloat(taskTimeEntryDuration) * 60);
      }
      if (taskTimeEntryUserId) {
        payload.user_id = parseInt(taskTimeEntryUserId, 10);
      }
      await api.post(`/internal/projects/tasks/${editingTaskId}/time-entries`, payload);
      setShowTaskTimeEntryForm(false);
      setTaskTimeEntryStart('');
      setTaskTimeEntryStop('');
      setTaskTimeEntryDuration('');
      setTaskTimeEntryNotes('');
      setTaskTimeEntryUserId('');
      fetchTaskTimeEntries(editingTaskId);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add time entry');
    }
  };

  const handleEditTaskTimeEntry = async (entryId: number) => {
    if (!editingTimeEntry) return;
    try {
      const payload: { notes?: string | null; start_time?: string; stop_time?: string; duration_minutes?: number } = { notes: editingTimeEntry.notes ?? null };
      if (editingTimeEntry.start_time && editingTimeEntry.stop_time) {
        payload.start_time = editingTimeEntry.start_time;
        payload.stop_time = editingTimeEntry.stop_time;
      } else if (editingTimeEntry.duration_minutes !== undefined) {
        payload.duration_minutes = editingTimeEntry.duration_minutes;
      }
      await api.put(`/internal/projects/tasks/time-entries/${entryId}`, payload);
      setEditingTimeEntryId(null);
      setEditingTimeEntry(null);
      if (editingTaskId) fetchTaskTimeEntries(editingTaskId);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update time entry');
    }
  };

  const handleDeleteTaskTimeEntry = async (entryId: number) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await api.delete(`/internal/projects/tasks/time-entries/${entryId}`);
      if (editingTaskId) fetchTaskTimeEntries(editingTaskId);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete time entry');
    }
  };

  const isTaskOverdue = (task: Task) => {
    if (!task.due_date) return false;
    const due = new Date(task.due_date);
    due.setHours(23, 59, 59, 999);
    return new Date() > due && task.status !== 'done';
  };

  // Filter tasks by assignee (project detail view)
  const filteredTasks = (() => {
    if (!assignedToFilter) return tasks;
    if (assignedToFilter === 'unassigned') return tasks.filter((t) => t.assigned_to == null);
    const id = parseInt(assignedToFilter, 10);
    if (Number.isNaN(id)) return tasks;
    return tasks.filter((t) => t.assigned_to === id);
  })();

  const getTasksByStatus = (status: TaskStatus) =>
    filteredTasks.filter((t) => t.status === status);

  // Filter projects by company (list view)
  const filteredProjects = (() => {
    if (!companyFilter) return projects;
    if (companyFilter === 'internal') return projects.filter((p) => p.company_id == null);
    const id = parseInt(companyFilter, 10);
    return Number.isNaN(id) ? projects : projects.filter((p) => p.company_id === id);
  })();

  // Filter my assigned tasks by company (same logic as filteredProjects)
  const filteredMyTasks = (() => {
    if (!companyFilter) return myTasks;
    const projectById = new Map(projects.map((p) => [p.id, p]));
    return myTasks.filter((task) => {
      const proj = projectById.get(task.project_id);
      if (!proj) return false;
      if (companyFilter === 'internal') return proj.company_id == null;
      const id = parseInt(companyFilter, 10);
      return !Number.isNaN(id) && proj.company_id === id;
    });
  })();

  // ——— Projects list view ———
  if (!isProjectView) {
    if (projectsLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      );
    }
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Projects
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Create projects and manage tasks in Table or Kanban view
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
            {error}
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="company-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Company:
              </label>
              <select
                id="company-filter"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm min-w-[160px]"
              >
                <option value="">All companies</option>
                <option value="internal">Internal</option>
                {companies.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.company_name || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View:</span>
              <button
                type="button"
                onClick={() => {
                  setProjectsListViewMode('cards');
                  localStorage.setItem(PROJECTS_VIEW_STORAGE_KEY, 'cards');
                }}
                className={`px-3 py-1.5 text-sm rounded border ${
                  projectsListViewMode === 'cards'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Cards
              </button>
              <button
                type="button"
                onClick={() => {
                  setProjectsListViewMode('table');
                  localStorage.setItem(PROJECTS_VIEW_STORAGE_KEY, 'table');
                }}
                className={`px-3 py-1.5 text-sm rounded border ${
                  projectsListViewMode === 'table'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                Table
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowAddProject(!showAddProject)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {showAddProject ? 'Cancel' : 'New project'}
          </button>
        </div>

        {showAddProject && (
          <form
            onSubmit={handleAddProject}
            className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">New project</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Project name"
                  className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder="Brief description..."
                  rows={2}
                  className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company
                </label>
                <select
                  value={newProjectCompanyId}
                  onChange={(e) => setNewProjectCompanyId(e.target.value)}
                  className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">Internal</option>
                  {companies.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.company_name || c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Shepherd (internal agent)
                </label>
                <select
                  value={newProjectShepherdId}
                  onChange={(e) => setNewProjectShepherdId(e.target.value)}
                  className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">No shepherd</option>
                  {agents.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name || a.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={projectSubmitting || !newProjectName.trim()}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {projectSubmitting ? 'Creating…' : 'Create project'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddProject(false)}
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}

        {showEditProjectForm && editingProjectId != null && (
          <form
            onSubmit={handleEditProject}
            className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Edit project</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editProjectName}
                  onChange={(e) => setEditProjectName(e.target.value)}
                  placeholder="Project name"
                  className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={editProjectDescription}
                  onChange={(e) => setEditProjectDescription(e.target.value)}
                  placeholder="Brief description..."
                  rows={2}
                  className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company
                </label>
                <select
                  value={editProjectCompanyId}
                  onChange={(e) => setEditProjectCompanyId(e.target.value)}
                  className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">Internal</option>
                  {companies.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.company_name || c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Shepherd (internal agent)
                </label>
                <select
                  value={editProjectShepherdId}
                  onChange={(e) => setEditProjectShepherdId(e.target.value)}
                  className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="">No shepherd</option>
                  {agents.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name || a.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={editProjectSubmitting || !editProjectName.trim()}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editProjectSubmitting ? 'Saving…' : 'Save changes'}
                </button>
                <button
                  type="button"
                  onClick={cancelEditProject}
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}

        {/* My assigned tasks across all projects */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
            My assigned tasks
          </h2>
          {myTasksLoading ? (
            <div className="flex items-center gap-2 py-4 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent" />
              <span className="text-sm">Loading your tasks…</span>
            </div>
          ) : filteredMyTasks.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-3">
              {companyFilter
                ? 'You have no assigned tasks in the selected company.'
                : 'You have no assigned tasks across any project.'}
            </p>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Task
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Project
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Due date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredMyTasks.map((task) => {
                      const overdue = isTaskOverdue(task);
                      const project = projects.find((p) => p.id === task.project_id);
                      const companyDisplay = project?.company_name ?? project?.company_contact_name ?? 'Internal';
                      return (
                        <tr
                          key={task.id}
                          className={
                            overdue
                              ? 'bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }
                        >
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/internal/projects/${task.project_id}`, {
                                  state: { openTaskId: task.id },
                                })
                              }
                              className="text-left font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              {task.title}
                            </button>
                            {task.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1 max-w-md">
                                {task.description}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => navigate(`/internal/projects/${task.project_id}`)}
                              className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              {task.project_name}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {companyDisplay}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                task.status === 'done'
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                                  : task.status === 'in_progress'
                                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                              }`}
                            >
                              {COLUMNS.find((c) => c.id === task.status)?.label ?? task.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {task.due_date ? (
                              <span
                                className={
                                  overdue
                                    ? 'text-red-600 dark:text-red-400 font-medium'
                                    : 'text-gray-500 dark:text-gray-400'
                                }
                              >
                                {new Date(task.due_date).toLocaleDateString()}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {projectsListViewMode === 'table' ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Shepherd
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Tasks
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredProjects.map((proj) => (
                    <tr key={proj.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => navigate(`/internal/projects/${proj.id}`)}
                          className="text-left font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          {proj.name}
                        </button>
                        {proj.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1 max-w-xs">
                            {proj.description}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {proj.company_name || proj.company_contact_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {proj.shepherd_name || proj.shepherd_email || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {Number(proj.total_tasks ?? 0)} total · To Do: {Number(proj.tasks_todo ?? 0)} · In Progress: {Number(proj.tasks_in_progress ?? 0)} · Done: {Number(proj.tasks_done ?? 0)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(proj.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => navigate(`/internal/projects/${proj.id}`)}
                            className="text-sm px-3 py-1.5 rounded border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                          >
                            Open
                          </button>
                          <button
                            type="button"
                            onClick={() => startEditProject(proj)}
                            className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteProject(proj.id)}
                            className="text-sm px-3 py-1.5 rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredProjects.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                {companyFilter ? 'No projects match the selected company.' : 'No projects yet. Create one to get started.'}
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map((proj) => (
              <div
                key={proj.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/internal/projects/${proj.id}`)}
                  className="w-full text-left p-5 block"
                >
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {proj.name}
                  </h2>
                  <div className="mt-1.5 space-y-0.5 text-xs">
                    {(proj.company_name || proj.company_contact_name) && (
                      <p className="text-indigo-600 dark:text-indigo-400">
                        Company: {proj.company_name || proj.company_contact_name}
                      </p>
                    )}
                    {(proj.shepherd_name || proj.shepherd_email) && (
                      <p className="text-amber-600 dark:text-amber-400">
                        Shepherd: {proj.shepherd_name || proj.shepherd_email}
                      </p>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                    <span title="Total tasks">{Number(proj.total_tasks ?? 0)} tasks</span>
                    <span>To Do: {Number(proj.tasks_todo ?? 0)}</span>
                    <span>In Progress: {Number(proj.tasks_in_progress ?? 0)}</span>
                    <span>Done: {Number(proj.tasks_done ?? 0)}</span>
                  </div>
                  {proj.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {proj.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    Updated {new Date(proj.updated_at).toLocaleDateString()}
                  </p>
                </button>
                <div className="px-5 pb-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/internal/projects/${proj.id}`)}
                    className="text-sm px-3 py-1.5 rounded border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                  >
                    Open
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditProject(proj);
                    }}
                    className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject(proj.id);
                    }}
                    className="text-sm px-3 py-1.5 rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {filteredProjects.length === 0 && !showAddProject && projectsListViewMode === 'cards' && (
          <p className="text-gray-500 dark:text-gray-400 mt-6">
            {companyFilter ? 'No projects match the selected company.' : 'No projects yet. Create one to get started.'}
          </p>
        )}
      </div>
    );
  }

  // ——— Project detail view (tasks + Table/Kanban) ———
  if (loading || !project) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/internal/projects')}
          className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline mb-2"
        >
          ← Back to projects
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {project.name}
          </h1>
          <button
            type="button"
            onClick={() => startEditProject(project)}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Edit project
          </button>
        </div>
        <div className="mt-0.5 space-y-0.5 text-sm">
          {(project.company_name || project.company_contact_name) && (
            <p className="text-indigo-600 dark:text-indigo-400">
              Company: {project.company_name || project.company_contact_name}
            </p>
          )}
          {(project.shepherd_name || project.shepherd_email) && (
            <p className="text-amber-600 dark:text-amber-400">
              Shepherd: {project.shepherd_name || project.shepherd_email}
            </p>
          )}
        </div>
        {project.description && (
          <p className="text-gray-500 dark:text-gray-400 mt-1">{project.description}</p>
        )}
      </div>

      {showEditProjectForm && editingProjectId === project.id && (
        <form
          onSubmit={handleEditProject}
          className="mb-6 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Edit project</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input
                type="text"
                value={editProjectName}
                onChange={(e) => setEditProjectName(e.target.value)}
                placeholder="Project name"
                className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
              <textarea
                value={editProjectDescription}
                onChange={(e) => setEditProjectDescription(e.target.value)}
                placeholder="Brief description..."
                rows={2}
                className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
              <select
                value={editProjectCompanyId}
                onChange={(e) => setEditProjectCompanyId(e.target.value)}
                className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Internal</option>
                {companies.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.company_name || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Shepherd (internal agent)</label>
              <select
                value={editProjectShepherdId}
                onChange={(e) => setEditProjectShepherdId(e.target.value)}
                className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">No shepherd</option>
                {agents.map((a) => (
                  <option key={a.id} value={String(a.id)}>
                    {a.name || a.email}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={editProjectSubmitting || !editProjectName.trim()}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editProjectSubmitting ? 'Saving…' : 'Save changes'}
              </button>
              <button
                type="button"
                onClick={cancelEditProject}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="assignee-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Assignee:
            </label>
            <select
              id="assignee-filter"
              value={assignedToFilter}
              onChange={(e) => setAssignedToFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm min-w-[160px]"
            >
              <option value="">All</option>
              <option value="unassigned">Unassigned</option>
              {agents.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.name || a.email}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View:</span>
            <button
              type="button"
              onClick={() => {
                setViewMode('table');
                localStorage.setItem(PROJECT_TASKS_VIEW_STORAGE_KEY, 'table');
              }}
              className={`px-3 py-1.5 text-sm rounded border ${
                viewMode === 'table'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('kanban');
                localStorage.setItem(PROJECT_TASKS_VIEW_STORAGE_KEY, 'kanban');
              }}
              className={`px-3 py-1.5 text-sm rounded border ${
                viewMode === 'kanban'
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Kanban
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowAddTask(!showAddTask)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {showAddTask ? 'Cancel' : 'Add task'}
        </button>
      </div>

      {showAddTask && (
        <form
          onSubmit={handleAddTask}
          className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">New task</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Task title"
                className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description (optional)
              </label>
              <MarkdownEditor
                value={newDescription}
                onChange={setNewDescription}
                placeholder="Details, formatting, images..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assign to (agent)
              </label>
              <select
                value={newAssignedTo}
                onChange={(e) => setNewAssignedTo(e.target.value)}
                className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Unassigned</option>
                {agents.map((a) => (
                  <option key={a.id} value={String(a.id)}>
                    {a.name || a.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Due date (optional)
              </label>
              <input
                type="datetime-local"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                className="block w-full max-w-md rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting || !newTitle.trim()}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Adding…' : 'Add task'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddTask(false)}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Edit Task modal */}
      {editingTaskId != null && editingTask && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={cancelEditTask} />
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit task</h3>
                  <button
                    type="button"
                    onClick={cancelEditTask}
                    className="shrink-0 p-1 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Close"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form onSubmit={handleSaveEditTask} className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingTask.title ?? ''}
                      onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <MarkdownEditor
                      value={editingTask.description ?? ''}
                      onChange={(v) => setEditingTask({ ...editingTask, description: v })}
                      placeholder="Optional details..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={editingTask.status ?? 'todo'}
                        onChange={(e) => setEditingTask({ ...editingTask, status: e.target.value as TaskStatus })}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm"
                      >
                        {COLUMNS.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assignee</label>
                      <select
                        value={editingTask.assigned_to ?? ''}
                        onChange={(e) => setEditingTask({ ...editingTask, assigned_to: e.target.value ? parseInt(e.target.value, 10) : null })}
                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm"
                      >
                        <option value="">Unassigned</option>
                        {agents.map((a) => (
                          <option key={a.id} value={String(a.id)}>{a.name || a.email}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due date</label>
                    <input
                      type="datetime-local"
                      value={editingTask.due_date ?? ''}
                      onChange={(e) => setEditingTask({ ...editingTask, due_date: e.target.value })}
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={editTaskSubmitting || !editingTask.title?.trim()} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50">
                      {editTaskSubmitting ? 'Saving…' : 'Save task'}
                    </button>
                    <button type="button" onClick={cancelEditTask} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600">
                      Cancel
                    </button>
                  </div>
                </form>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                  <h4 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-4">Comments</h4>
                  <form onSubmit={handleAddTaskComment} className="mb-4">
                    <MarkdownEditor
                      value={newCommentMessage}
                      onChange={setNewCommentMessage}
                      placeholder="Add a comment... (supports markdown, images)"
                    />
                    <button type="submit" disabled={commentSubmitting || !newCommentMessage.trim()} className="mt-2 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50">
                      {commentSubmitting ? 'Posting…' : 'Post comment'}
                    </button>
                  </form>
                  <div className="space-y-3 max-h-40 overflow-y-auto">
                    {taskComments.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No comments yet.</p>}
                    {taskComments.map((c) => (
                      <div key={c.id} className="py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm">
                        <div className="prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-gray-100">
                          <MarkdownRenderer content={c.message} className="prose-p:my-0.5 prose-headings:my-0.5" />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{c.user_name || c.user_email} · {new Date(c.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Time tracking</h4>
                    {!showTaskTimeEntryForm && (
                      <button type="button" onClick={() => setShowTaskTimeEntryForm(true)} className="text-sm px-3 py-1.5 rounded border border-indigo-600 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                        Add time entry
                      </button>
                    )}
                  </div>
                  {taskTimeEntries.length > 0 && (
                    <div className="mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Total: <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{formatDuration(taskTimeEntries.reduce((s, e) => s + e.duration_minutes, 0))}</span>
                      </p>
                    </div>
                  )}
                  {showTaskTimeEntryForm && (
                    <form onSubmit={handleAddTaskTimeEntry} className="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                      {agents.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">User</label>
                          <select value={taskTimeEntryUserId} onChange={(e) => setTaskTimeEntryUserId(e.target.value)} className="block w-full max-w-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm">
                            <option value="">Me (current user)</option>
                            {agents.map((a) => (
                              <option key={a.id} value={String(a.id)}>{a.name ? `${a.name} (${a.email})` : a.email}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="flex gap-4">
                        <label className="flex items-center"><input type="radio" checked={taskTimeEntryMode === 'start_stop'} onChange={() => { setTaskTimeEntryMode('start_stop'); setTaskTimeEntryDuration(''); }} className="mr-2" /> Start/Stop</label>
                        <label className="flex items-center"><input type="radio" checked={taskTimeEntryMode === 'direct'} onChange={() => { setTaskTimeEntryMode('direct'); setTaskTimeEntryStart(''); setTaskTimeEntryStop(''); }} className="mr-2" /> Direct (hours)</label>
                      </div>
                      {taskTimeEntryMode === 'start_stop' ? (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Start</label>
                            <input type="datetime-local" value={taskTimeEntryStart} onChange={(e) => setTaskTimeEntryStart(e.target.value)} className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" required={taskTimeEntryMode === 'start_stop'} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Stop</label>
                            <input type="datetime-local" value={taskTimeEntryStop} onChange={(e) => setTaskTimeEntryStop(e.target.value)} className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" required={taskTimeEntryMode === 'start_stop'} />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Duration (hours)</label>
                          <input type="number" step="0.25" min="0" value={taskTimeEntryDuration} onChange={(e) => setTaskTimeEntryDuration(e.target.value)} className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" placeholder="e.g. 1.5" required={taskTimeEntryMode === 'direct'} />
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes</label>
                        <textarea value={taskTimeEntryNotes} onChange={(e) => setTaskTimeEntryNotes(e.target.value)} rows={2} className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" />
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">Add</button>
                        <button type="button" onClick={() => { setShowTaskTimeEntryForm(false); setTaskTimeEntryUserId(''); }} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded">Cancel</button>
                      </div>
                    </form>
                  )}
                  <div className="space-y-2">
                    {taskTimeEntries.length === 0 && !showTaskTimeEntryForm && <p className="text-sm text-gray-500 dark:text-gray-400">No time entries yet.</p>}
                    {taskTimeEntries.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded text-sm">
                        {editingTimeEntryId === entry.id ? (
                          <div className="flex-1 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <input type="datetime-local" value={editingTimeEntry?.start_time ? new Date(editingTimeEntry.start_time).toISOString().slice(0, 16) : ''} onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, start_time: e.target.value ? new Date(e.target.value).toISOString() : undefined })} className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs" />
                              <input type="datetime-local" value={editingTimeEntry?.stop_time ? new Date(editingTimeEntry.stop_time).toISOString().slice(0, 16) : ''} onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, stop_time: e.target.value ? new Date(e.target.value).toISOString() : undefined })} className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs" />
                            </div>
                            <input type="number" step="0.25" value={editingTimeEntry?.duration_minutes != null ? (editingTimeEntry.duration_minutes / 60).toFixed(2) : ''} onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, duration_minutes: Math.round(parseFloat(e.target.value || '0') * 60) })} className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs w-24" placeholder="Hours" />
                            <textarea value={editingTimeEntry?.notes ?? ''} onChange={(e) => setEditingTimeEntry({ ...editingTimeEntry, notes: e.target.value })} rows={1} className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs" />
                            <div className="flex gap-2">
                              <button type="button" onClick={() => handleEditTaskTimeEntry(entry.id)} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">Save</button>
                              <button type="button" onClick={() => { setEditingTimeEntryId(null); setEditingTimeEntry(null); }} className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="text-gray-700 dark:text-gray-300">{formatDuration(entry.duration_minutes)}</span>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">{entry.user_name || entry.user_email}</span>
                            {entry.notes && <span className="text-gray-500 dark:text-gray-400 text-xs truncate max-w-[120px]" title={entry.notes}>{entry.notes}</span>}
                            <div className="flex gap-1">
                              <button type="button" onClick={() => { setEditingTimeEntryId(entry.id); setEditingTimeEntry({ start_time: entry.start_time ?? undefined, stop_time: entry.stop_time ?? undefined, duration_minutes: entry.duration_minutes, notes: entry.notes ?? undefined }); }} className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600">Edit</button>
                              <button type="button" onClick={() => handleDeleteTaskTimeEntry(entry.id)} className="text-xs px-2 py-0.5 rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">Delete</button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'table' ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Assignee
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Due
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTasks.map((task) => (
                  <tr key={task.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isTaskOverdue(task) ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                      {task.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-h-16 overflow-hidden prose prose-sm dark:prose-invert max-w-none">
                          <MarkdownRenderer content={task.description} className="prose-p:my-0.5 prose-headings:my-0.5" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={task.assigned_to ?? ''}
                        onChange={(e) => handleAssignTask(task.id, e.target.value ? parseInt(e.target.value, 10) : null)}
                        className="text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[120px]"
                      >
                        <option value="">Unassigned</option>
                        {agents.map((a) => (
                          <option key={a.id} value={String(a.id)}>
                            {a.name || a.email}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={task.status}
                        onChange={(e) => handleMoveTask(task.id, e.target.value as TaskStatus)}
                        disabled={moveTargetId === task.id}
                        className="text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        {COLUMNS.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {task.due_date ? (
                        <span className={isTaskOverdue(task) ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(task.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => openEditTask(task)}
                          className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteTask(task.id)}
                          className="text-xs px-2 py-1 rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredTasks.length === 0 && (
            <p className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              {assignedToFilter ? 'No tasks match the selected assignee.' : 'No tasks yet. Add one above.'}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {COLUMNS.map((col) => (
            <div
              key={col.id}
              className={`rounded-xl border-2 ${col.color} min-h-[320px] flex flex-col ${draggedTaskId ? 'ring-2 ring-indigo-400 ring-offset-2' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => {
                e.preventDefault();
                const taskId = e.dataTransfer.getData('text/plain');
                if (taskId) {
                  const id = parseInt(taskId, 10);
                  const task = tasks.find((t) => t.id === id);
                  if (task && task.status !== col.id) {
                    handleMoveTask(id, col.id);
                  }
                }
                setDraggedTaskId(null);
              }}
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {col.label}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {getTasksByStatus(col.id).length} task
                  {getTasksByStatus(col.id).length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="p-3 flex-1 space-y-3 overflow-y-auto min-h-[200px]">
                {getTasksByStatus(col.id).map((task) => (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={(e) => {
                      setDraggedTaskId(task.id);
                      e.dataTransfer.setData('text/plain', String(task.id));
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => setDraggedTaskId(null)}
                    className={`bg-white dark:bg-gray-800 rounded-lg shadow border p-3 hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing ${draggedTaskId === task.id ? 'opacity-50' : ''} ${isTaskOverdue(task) ? 'border-red-400 dark:border-red-600 bg-red-50/50 dark:bg-red-900/10' : 'border-gray-200 dark:border-gray-600'}`}
                  >
                    <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                      {task.title}
                    </p>
                    {(task.assignee_name || task.assignee_email) && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                        Assignee: {task.assignee_name || task.assignee_email}
                      </p>
                    )}
                    {task.due_date && (
                      <p className={`text-xs mt-1 ${isTaskOverdue(task) ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                        Due: {new Date(task.due_date).toLocaleDateString()}
                      </p>
                    )}
                    {task.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-3 prose prose-sm dark:prose-invert max-w-none prose-p:my-0.5 prose-headings:my-0.5">
                        <MarkdownRenderer content={task.description} />
                      </div>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                      {new Date(task.updated_at).toLocaleDateString()}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openEditTask(task); }}
                        className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        Edit
                      </button>
                      <select
                        value={task.assigned_to ?? ''}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleAssignTask(task.id, e.target.value ? parseInt(e.target.value, 10) : null);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-1"
                      >
                        <option value="">Unassigned</option>
                        {agents.map((a) => (
                          <option key={a.id} value={String(a.id)}>
                            {a.name || a.email}
                          </option>
                        ))}
                      </select>
                      {COLUMNS.filter((c) => c.id !== task.status).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleMoveTask(task.id, c.id); }}
                          disabled={moveTargetId === task.id}
                          className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                        >
                          → {c.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                        className="text-xs px-2 py-1 rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InternalProjects;
